const STORAGE_KEY = 'oh_pedidos_offline_queue_v1';
const HISTORY_KEY = 'oh_pedidos_offline_history_v1';
const HISTORY_LIMIT = 40;

function toMysqlDatetimeLocal(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    const ms = value < 1e12 ? value * 1000 : value;
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return null;
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
  }
  const s = String(value).trim();
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(\.\d+)?$/.test(s)) return s.slice(0, 19);
  const d = value instanceof Date ? value : new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
}

/** Evita ISO en visita_* al reenviar cola offline a API vieja/nueva. */
function sanitizePedidoPayloadDates(payload) {
  if (!payload || typeof payload !== 'object') return payload;
  const out = { ...payload };
  if (out.visita_inicio != null) out.visita_inicio = toMysqlDatetimeLocal(out.visita_inicio);
  if (out.visita_fin != null) out.visita_fin = toMysqlDatetimeLocal(out.visita_fin);
  return out;
}

function safeParse(raw) {
  try {
    const data = JSON.parse(raw);
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

export function loadOfflineQueue(vendedorId = null) {
  try {
    const rows = safeParse(localStorage.getItem(STORAGE_KEY) || '[]');
    if (!vendedorId) return rows;
    return rows.filter((row) => Number(row.vendedor_id) === Number(vendedorId));
  } catch {
    return [];
  }
}

function saveOfflineQueue(rows) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
  } catch {
    throw new Error(
      'No hay espacio en la tablet para guardar el pedido. Libera espacio e intenta de nuevo.'
    );
  }
}

export function offlineQueueCount(vendedorId = null) {
  return loadOfflineQueue(vendedorId).length;
}

export function loadOfflineHistory(vendedorId = null) {
  try {
    const rows = safeParse(localStorage.getItem(HISTORY_KEY) || '[]');
    if (!vendedorId) return rows;
    return rows.filter((row) => Number(row.vendedor_id) === Number(vendedorId));
  } catch {
    return [];
  }
}

function addOfflineHistory(entry, pedido) {
  const rows = loadOfflineHistory();
  rows.unshift({
    id: entry.id,
    vendedor_id: entry.vendedor_id,
    cliente_nombre: entry.cliente_nombre,
    items_count: entry.items_count,
    subtotal: entry.subtotal,
    queued_at: entry.created_at,
    sent_at: new Date().toISOString(),
    pedido_id: pedido?.id || null,
    numero: pedido?.numero || null,
    estado: pedido?.estado || 'pendiente',
  });
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(rows.slice(0, HISTORY_LIMIT)));
  } catch {
    // El pedido ya llegó al servidor; no bloquear por falta de espacio en el historial local.
  }
}

export function clearOfflineHistory(vendedorId) {
  const keep = vendedorId
    ? loadOfflineHistory().filter((row) => Number(row.vendedor_id) !== Number(vendedorId))
    : [];
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(keep));
  } catch {
    /* ignore */
  }
}

export function isNetworkError(err) {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  if (err?.name === 'AbortError') return true;
  const msg = String(err?.message || err || '');
  return /No se pudo conectar|Failed to fetch|NetworkError|ERR_INTERNET|ERR_NETWORK|conexión|offline|Load failed|network|tardó demasiado|AbortError|timeout/i.test(
    msg
  );
}

export function isInvalidVisitError(err) {
  const status = Number(err?.status);
  const message = String(err?.message || '');
  return [400, 404, 409].includes(status) && /visita/i.test(message);
}

function newLocalId() {
  return `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Guarda un pedido en la tablet para enviarlo después a oficina.
 * @param {{ holdUntilManual?: boolean }} [opts] — si true, no se auto-envía; solo con «Enviar ahora».
 * @returns {{ id, created_at, cliente_nombre, items_count, subtotal, hold_until_manual }}
 */
export function enqueueOfflinePedido({
  payload,
  subtotal = 0,
  vendedorId = null,
  holdUntilManual = false,
} = {}) {
  const rows = loadOfflineQueue();
  const entry = {
    id: newLocalId(),
    created_at: new Date().toISOString(),
    vendedor_id: vendedorId,
    cliente_nombre: payload.cliente_nombre || 'Cliente',
    items_count: (payload.items || []).reduce((n, i) => n + Number(i.cantidad || 0), 0),
    subtotal: Number(subtotal) || 0,
    payload,
    attempts: 0,
    last_error: null,
    hold_until_manual: Boolean(holdUntilManual),
  };
  rows.push(entry);
  saveOfflineQueue(rows);
  return entry;
}

export function removeOfflinePedido(id) {
  saveOfflineQueue(loadOfflineQueue().filter((r) => r.id !== id));
}

export function updateOfflinePedido(id, patch) {
  const rows = loadOfflineQueue().map((r) => (r.id === id ? { ...r, ...patch } : r));
  saveOfflineQueue(rows);
}

/**
 * Envía la cola FIFO. Se detiene en el primer fallo de red.
 * @param {{ vendedorId?: *, includeHeld?: boolean }} [opts]
 *   includeHeld=false (auto): omite pedidos «Guardar en tablet» hasta que el vendedor pulse Enviar.
 * @returns {{ sent: object[], remaining: number, stopped: boolean }}
 */
export async function flushOfflineQueue(crearPedidoFn, { vendedorId, includeHeld = false } = {}) {
  const queue = loadOfflineQueue(vendedorId);
  const sent = [];
  for (const entry of queue) {
    if (entry.hold_until_manual && !includeHeld) continue;
    const trySend = async (payload) => crearPedidoFn(payload);
    const payloadConId = sanitizePedidoPayloadDates({
      ...entry.payload,
      client_request_id: entry.payload?.client_request_id || entry.id,
    });
    try {
      const res = await trySend(payloadConId);
      addOfflineHistory(entry, res.data);
      removeOfflinePedido(entry.id);
      sent.push({ entry, pedido: res.data });
      continue;
    } catch (err) {
      // Visita vieja / inexistente: reintentar sin visita_id
      if (isInvalidVisitError(err) && payloadConId.visita_id) {
        try {
          const payload = sanitizePedidoPayloadDates({ ...payloadConId, visita_id: null });
          const res = await trySend(payload);
          addOfflineHistory(entry, res.data);
          removeOfflinePedido(entry.id);
          sent.push({ entry, pedido: res.data });
          continue;
        } catch (err2) {
          updateOfflinePedido(entry.id, {
            attempts: (entry.attempts || 0) + 1,
            last_error: err2.message || err.message || 'Error al enviar',
          });
          if (isNetworkError(err2)) {
            return { sent, remaining: offlineQueueCount(vendedorId), stopped: true };
          }
          continue;
        }
      }
      updateOfflinePedido(entry.id, {
        attempts: (entry.attempts || 0) + 1,
        last_error: err.message || 'Error al enviar',
      });
      if (isNetworkError(err)) {
        return { sent, remaining: offlineQueueCount(vendedorId), stopped: true };
      }
      // Error de validación del servidor: dejar en cola pero seguir con el siguiente
    }
  }
  return { sent, remaining: offlineQueueCount(vendedorId), stopped: false };
}
